import React from 'react';
import logger from '../utils/logger';

/**
 * Error Boundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing.
 * 
 * This prevents the entire app from crashing when a component error occurs.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    logger.error('[ErrorBoundary] Caught error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });

    // Store error details in state for display
    this.setState({
      error,
      errorInfo
    });

    // You can also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    // A failed lazy-chunk import stays rejected in React's cache — clearing
    // state would just re-throw immediately. Only a reload re-fetches it.
    const message = this.state.error?.message || '';
    const isChunkError =
      this.state.error?.name === 'ChunkLoadError' ||
      /loading chunk|dynamically imported module|import\(\) failed/i.test(message);
    if (isChunkError) {
      window.location.reload();
      return;
    }

    // Reset error state so children re-render
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      // Mounted above App, so no language prop — read the persisted
      // preference directly to keep the crash screen bilingual
      let isSpanish = false;
      try {
        isSpanish = localStorage.getItem('compudopt_language') === 'es';
      } catch (e) {
        // Storage unavailable — default to English
      }
      const copy = isSpanish
        ? {
            title: 'Algo salió mal',
            body: 'Lo sentimos, ocurrió algo inesperado. Por favor intenta recargar la página.',
            tryAgain: 'Intentar de Nuevo',
            reload: 'Recargar Página',
          }
        : {
            title: 'Something went wrong',
            body: "We're sorry, but something unexpected happened. Please try refreshing the page.",
            tryAgain: 'Try Again',
            reload: 'Reload Page',
          };

      // Custom fallback UI
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          background: '#F8F9FA',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          <div style={{
            maxWidth: '600px',
            width: '100%',
            background: 'white',
            borderRadius: '12px',
            padding: '40px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}>
            <h1 style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--text-primary)',
              marginBottom: '16px'
            }}>
              {copy.title}
            </h1>

            <p style={{
              fontSize: 'var(--text-base)',
              color: 'var(--text-secondary)',
              marginBottom: '24px',
              lineHeight: '1.6'
            }}>
              {copy.body}
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{
                marginTop: '24px',
                padding: '16px',
                background: 'var(--bg-tertiary)',
                borderRadius: '8px',
                textAlign: 'left',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-secondary)',
                maxHeight: '300px',
                overflow: 'auto'
              }}>
                <summary style={{
                  cursor: 'pointer',
                  fontWeight: '600',
                  marginBottom: '8px'
                }}>
                  Error Details (Development Only)
                </summary>
                <pre style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  margin: 0
                }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div style={{ marginTop: '24px' }}>
              <button
                onClick={this.handleReset}
                style={{
                  background: 'var(--compudopt-green)',
                  color: 'var(--bg-primary)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  padding: '12px 24px',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-semibold)',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = 'var(--green-dark)'}
                onMouseLeave={(e) => e.target.style.background = 'var(--compudopt-green)'}
              >
                {copy.tryAgain}
              </button>

              <button
                onClick={() => window.location.reload()}
                style={{
                  background: 'transparent',
                  color: 'var(--compudopt-green)',
                  border: '1px solid var(--compudopt-green)',
                  borderRadius: 'var(--radius-md)',
                  padding: '12px 24px',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-semibold)',
                  cursor: 'pointer',
                  marginLeft: '12px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'var(--bg-secondary)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                }}
              >
                {copy.reload}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Render children normally if no error
    return this.props.children;
  }
}

export default ErrorBoundary;
